import React, { useCallback, useMemo } from 'react'
import { View, Text, Platform } from 'react-native'
import { ChevronDown } from 'lucide-react-native'
import { Dropdown } from 'react-native-element-dropdown'
import { useTheme } from '../../../src/theme/useTheme'
import { useDropdownOverlayPosition } from '../../../src/shared/hooks/useDropdownOverlayPosition'
import { CountryFlag } from '../../worldcup/CountryFlag/CountryFlag'
import { resolveLeagueCountryCodeForFlag } from '../../../src/shared/helpers/leagueCountryToFlagCode.helper'
import { getLeagueSelectorStyles, LEAGUE_LABEL } from './LeagueSelector.styles'
import type { LeagueOption } from '../../../src/gameResults/helpers/gameResultsApi'

type LeagueDropdownItem = { label: string; value: string; country?: string }

interface LeagueSelectorProps {
  availableLeagues: LeagueOption[]
  selectedLeague: string
  onLeagueChange: (leagueId: string) => void
}

export const LeagueSelector: React.FC<LeagueSelectorProps> = ({
  availableLeagues,
  selectedLeague,
  onLeagueChange,
}) => {
  const theme = useTheme()
  const styles = useMemo(() => getLeagueSelectorStyles(theme), [theme])

  const { triggerRef, webOverlayStyle, measure } = useDropdownOverlayPosition({
    overlayWidth: 300,
    alignment: 'left',
  })

  const dropdownData = useMemo<LeagueDropdownItem[]>(
    () =>
      availableLeagues.map((league) => ({
        label: league.name,
        value: league.id,
        country: league.country,
      })),
    [availableLeagues],
  )

  const selectedLeagueMeta = useMemo(
    () => availableLeagues.find((l) => l.id === selectedLeague),
    [availableLeagues, selectedLeague],
  )

  const selectedLeagueFlagCode = useMemo(
    () =>
      resolveLeagueCountryCodeForFlag(selectedLeagueMeta?.country, selectedLeagueMeta?.name),
    [selectedLeagueMeta?.country, selectedLeagueMeta?.name],
  )

  const renderDropdownItem = useCallback(
    (item: LeagueDropdownItem) => {
      const code = resolveLeagueCountryCodeForFlag(item.country, item.label)
      return (
        <View style={styles.dropdownItemRow}>
          {code != null ? (
            <CountryFlag
              countryCode={code}
              size="compact"
              accessibilityLabel={`${item.label} country flag`}
            />
          ) : (
            <View style={styles.dropdownItemFlagSpacer} />
          )}
          <Text style={styles.dropdownItemLabelText}>{item.label}</Text>
        </View>
      )
    },
    [styles.dropdownItemFlagSpacer, styles.dropdownItemLabelText, styles.dropdownItemRow],
  )

  const dropdownFlatListProps = useMemo(
    () => ({
      nestedScrollEnabled: true,
      ...(Platform.OS === 'web' ? { style: styles.dropdownFlatListWeb } : {}),
    }),
    [styles.dropdownFlatListWeb],
  )

  return (
    <View ref={triggerRef} style={styles.container}>
      <Text style={styles.label}>{LEAGUE_LABEL}</Text>
      <Dropdown
        style={styles.dropdown}
        backgroundColor={theme.background.overlay}
        containerStyle={[
          styles.dropdownOverlay,
          Platform.OS === 'web' && styles.dropdownOverlayWeb,
          webOverlayStyle as React.ComponentProps<typeof Dropdown>['containerStyle'],
        ]}
        placeholderStyle={styles.placeholderText}
        selectedTextStyle={styles.selectedText}
        itemContainerStyle={styles.itemContainer}
        itemTextStyle={styles.itemText}
        activeColor={theme.primary + '18'}
        dropdownPosition="bottom"
        showsVerticalScrollIndicator={false}
        flatListProps={dropdownFlatListProps}
        data={dropdownData}
        maxHeight={320}
        labelField="label"
        valueField="value"
        placeholder="Select League"
        value={selectedLeague}
        onChange={(item) => onLeagueChange(item.value)}
        onFocus={measure}
        renderItem={renderDropdownItem}
        renderLeftIcon={() =>
          selectedLeagueFlagCode != null ? (
            <View style={styles.dropdownTriggerFlagWrap}>
              <CountryFlag
                countryCode={selectedLeagueFlagCode}
                size="compact"
                accessibilityLabel={
                  selectedLeagueMeta
                    ? `${selectedLeagueMeta.name} country flag`
                    : 'League country flag'
                }
              />
            </View>
          ) : null
        }
        renderRightIcon={() => (
          <ChevronDown size={24} color={theme.gray[600]} style={styles.dropdownChevron} />
        )}
      />
    </View>
  )
}
